import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

export const leadContact = (lead) => ({
  contactName: lead?.personName || lead?.contactName || lead?.name || "Contact",
  contactTitle: lead?.personTitle || lead?.contactTitle || "",
  companyName: lead?.companyName || lead?.company || "",
  linkedinUrl: lead?.linkedinUrl || lead?.url || null,
  // Resolve best email from lead data
  bestEmail: lead?.email || lead?.contactEmail || (lead?.allEmails?.length > 0 ? lead.allEmails[0] : "") || "",
});

const RESEND_TEST_MODE = "Resend is in test mode — you can only send to your own verified email right now. To send to any recipient, verify your domain at resend.com/domains.";

export const useOutreach = ({ lead, userId }) => {
  const contact = leadContact(lead);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [outreach, setOutreach] = useState(null);
  const [error, setError] = useState(null);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [copiedLinkedin, setCopiedLinkedin] = useState(false);

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    setSent(false);
    try {
      const res = await axios.post(`${API}/outreach/generate-outreach`, {
        userId, contactName: contact.contactName, contactTitle: contact.contactTitle, companyName: contact.companyName,
        companyUrl: lead?.website || null, linkedinUrl: contact.linkedinUrl, triggerEvent: lead?.whyGoodFit?.[0] || lead?.expansionAngle || null,
      });
      const data = res.data;
      setOutreach(data);
      // Use lead email first, then API-generated, then fallback
      setRecipientEmail(contact.bestEmail || data.email?.recipientEmail || "");
      setSubject(data.email?.subject || "");
      setBody(data.email?.body || "");
    } catch (err) {
      console.error("Outreach generation failed:", err);
      setError("Failed to generate outreach. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const handleSend = async () => {
    if (!recipientEmail.trim()) return toast.error("Recipient email is required");
    if (!subject.trim()) return toast.error("Subject is required");
    setSending(true);
    setError(null);
    try {
      await axios.post(`${API}/outreach/send-email`, { userId, recipientEmail: recipientEmail.trim(), recipientName: contact.contactName, subject: subject.trim(), body: body.trim() });
      setSent(true);
      toast.success(`Email sent to ${recipientEmail}`);
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to send email";
      // Detect Resend test-mode restriction
      setError(detail.includes("only send testing emails") || detail.includes("verify a domain") ? RESEND_TEST_MODE : detail);
    } finally {
      setSending(false);
    }
  };

  const handleCopyLinkedin = () => {
    navigator.clipboard.writeText(outreach?.linkedin?.followUpMessage || "");
    setCopiedLinkedin(true);
    toast.success("LinkedIn message copied!");
    setTimeout(() => setCopiedLinkedin(false), 2000);
  };

  return {
    contact, generating, sending, sent, outreach, error, copiedLinkedin,
    email: { recipientEmail, setRecipientEmail, subject, setSubject, body, setBody },
    handleGenerate, handleSend, handleCopyLinkedin,
  };
};
